import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buttonClasses,
  formClasses,
  inputClasses,
  buttonRowClasses,
} from 'styles/tailwindClassnames';
import RequiredTag from 'shared/components/forms/RequiredTag';

// The create-a-device fields, shared by the rules editor (inline, below its list) and
// the Sources tab's slide-out. One set of fields, one validation story, one 409
// message — the two copies had already drifted once.
//
// The caller owns the request (and infers the provider); a rejection keeps the form
// open with the typing intact, and a 409 is explained rather than genericized.

type CreateDeviceFormProps = {
  // Pre-fills the ID field (the editor passes the unmatched search term).
  initialExternalId?: string;
  onSubmit: (payload: { external_id: string; name?: string }) => Promise<void>;
  onCancel: () => void;
  // Wraps testids so each surface stays addressable in tests.
  testIdPrefix: string;
};

const CreateDeviceForm = ({
  initialExternalId = '',
  onSubmit,
  onCancel,
  testIdPrefix,
}: CreateDeviceFormProps) => {
  const { t } = useTranslation();
  const [externalId, setExternalId] = useState(initialExternalId);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    const id = externalId.trim();
    if (!id || saving) return;
    setSaving(true);
    setError('');
    try {
      await onSubmit({
        external_id: id,
        ...(name.trim() ? { name: name.trim() } : {}),
      });
    } catch (err: any) {
      setError(
        err?.response?.status === 409
          ? t(
              'That source already exists for this provider. Search for it instead.',
            )
          : t('Could not add that source.'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="flex w-full flex-col">
        <label
          className="mb-1 text-ui-text-primary-text transition-colors duration-300"
          htmlFor={`${testIdPrefix}-device-id`}
        >
          {t('Source ID')}
          <RequiredTag />
        </label>
        <input
          id={`${testIdPrefix}-device-id`}
          className={error ? inputClasses.error : inputClasses.standard}
          type="text"
          autoComplete="off"
          value={externalId}
          onChange={(event) => {
            setExternalId(event.target.value);
            setError('');
          }}
          data-testid={`${testIdPrefix}-create-id`}
        />
        <span className="mt-1 text-body-sm text-ui-text-secondary-text">
          {t(
            'Must match exactly what the source sends. Unique within this provider.',
          )}
        </span>
      </div>
      <div className="flex w-full flex-col">
        <label
          className="mb-1 text-ui-text-primary-text transition-colors duration-300"
          htmlFor={`${testIdPrefix}-device-name`}
        >
          {t('Name')}
        </label>
        <input
          id={`${testIdPrefix}-device-name`}
          className={inputClasses.standard}
          type="text"
          autoComplete="off"
          value={name}
          onChange={(event) => setName(event.target.value)}
          data-testid={`${testIdPrefix}-create-name`}
        />
      </div>
      {error && <span className={formClasses.error}>{error}</span>}
      {/* Laid out like ModalShell's footer (right-aligned, primary rightmost,
          40px below the content once the parent's 16px gap is counted). The
          buttons stay here because they read the form's own state. */}
      <div className={`mt-6 ${buttonRowClasses.rowReverse}`}>
        <button
          type="button"
          onClick={submit}
          disabled={saving || !externalId.trim()}
          className={buttonClasses.primaryAction}
          data-testid={`${testIdPrefix}-add-by-id`}
        >
          {t('Add source')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className={buttonClasses.cancelAction}
          data-testid={`${testIdPrefix}-create-cancel`}
        >
          {t('Cancel')}
        </button>
      </div>
    </>
  );
};

export default CreateDeviceForm;
